import {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {Box} from '@mui/material';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {t} from 'i18next';

import JobResultHeader from './job-result-header/JobResultHeader';
import JobResultTabs, {
  JobResultTabConfig,
} from './job-result-tabs/JobResultTabs';
import JobResultDataGrid from './data-grid/JobResultDataGrid';
import ViewByDocsDataGrid from './data-grid/ViewByDocsDataGrid';
import './JobResults.scss';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import DocErrorPanel from './job-error-panel/DocErrorPanel';
import RelevancyDocsPanel from './job-relevancy-doc-panel/RelevancyDocsPanel';
import {
  REVIEW_JOB_QUERY_KEY,
  useGetAIReviewJobDetails,
  useGetAIReviewJobResult,
  useGetDocListMasterIdJobDocs,
  useGetMasterDocIdForIssueCoding,
  useGetMasterDocIdForRelevancy,
  useGetMasterDocIdErrorDoc,
} from '../../../../hooks/ai/review/useReview';
import {
  IGetDocListMasterIdJobDocsPayload,
  IGetMasterDocIdForIssueCodingPayload,
  IGetMasterDocIdForRelevancyPayload,
  IAIReviewRelevancyDocSelection,
  IAIReviewRelevancyDocSummaryRequest,
} from '../../../../api/types/aiReview.type';
import {
  BgNavigationTypes,
  SourceTypeCode,
  ToastType,
} from '../../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB} from '../../../../shared/types';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrDocMaxLimitWarning from '../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {useQueryClient} from '@tanstack/react-query';
import DocPreviewModal from '../../../document-view-prizmdoc/DocPreviewModal';

export type JobResultViewType = 'Issue Coding' | 'Relevancy';

const normalizeJobResultType = (jobType: unknown): JobResultViewType => {
  const normalized = String(jobType ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

  if (
    normalized === 'ISSUECODE' ||
    normalized === 'ISSUECODING' ||
    normalized === 'AIREVIEWJOBTYPEISSUECODING'
  ) {
    return 'Issue Coding';
  }

  return 'Relevancy';
};

export enum PAYLOAD_TYPE {
  REVIEW_MATRIX,
  DOC_RESULT_RELEVANCY,
  DOC_RESULT_ISSUE_CODE,
  DOC_ERROR,
}

export type IPayload =
  | IGetMasterDocIdForRelevancyPayload
  | IGetMasterDocIdForIssueCodingPayload
  | IGetDocListMasterIdJobDocsPayload;

export interface IPreviewDoc {
  id: number;
  docId: string;
}

const JobResults = () => {
  const [searchParams, _] = useSearchParams();
  const jobId = searchParams.get('jobId') as string;

  const [activeResultTab, setActiveResultTab] = useState(0);
  const [isDocErrorPanelOpen, setIsDocErrorPanelOpen] = useState(false);
  const [isRelevancyDocsPanelOpen, setIsRelevancyDocsPanelOpen] =
    useState(false);
  const [selectedRelevancyDoc, setSelectedRelevancyDoc] = useState<
    (IAIReviewRelevancyDocSummaryRequest & {docId: string}) | null
  >(null);
  const [payloadAndType, setPayloadAndType] = useState<{
    payload: any;
    type: PAYLOAD_TYPE;
  } | null>(null);
  const [showDocLimitWarning, setShowDocLimitWarning] = useState(false);
  const [docNavDestination, setDocNavDestination] = useState<
    typeof SourceTypeCode.DOCLIST | typeof SourceTypeCode.DOCVIEW
  >(SourceTypeCode.DOCLIST);
  const [viewInDocListAction, setViewInDocListAction] = useState<{
    enabled: boolean;
    onClick: () => void;
  }>({enabled: false, onClick: () => {}});
  const [goToDocListPayload, setGoToDocListPayload] =
    useState<IGetDocListMasterIdJobDocsPayload>({
      reviewJobId: null,
      type: 'all',
    });
  const [isNavigatingToDocList, setIsNavigatingToDocList] = useState(false);
  const [toastDetails, setToastDetails] = useState<{
    isOpen: boolean;
    message: string;
    status: ToastType;
  }>({
    isOpen: false,
    message: '',
    status: ToastType.Success,
  });
  const [masterDocListID, setMasterDocListID] = useState<number | null>(null);
  const [selectedDocForPreview, setSelectedDocForPreview] =
    useState<IPreviewDoc | null>(null);
  const {data: getJobDetailData, refetch: refetchJobDetails} =
    useGetAIReviewJobDetails({jobId: jobId!}, !!jobId);

  const normalizedJobType = useMemo(() => {
    const jobTypeFromParams = searchParams.get('jobType');
    if (jobTypeFromParams) {
      return normalizeJobResultType(jobTypeFromParams);
    }

    if (getJobDetailData?.jobType) {
      return normalizeJobResultType(getJobDetailData.jobType);
    }

    return 'Relevancy';
  }, [searchParams, getJobDetailData?.jobType]);

  const isIssueCodingJob = normalizedJobType === 'Issue Coding';
  const hasGroundTruthSelection = Boolean(getJobDetailData?.groundTruthTagID);

  const {data: reviewJobResult} = useGetAIReviewJobResult({
    jobId: jobId,
    evaluationType: isIssueCodingJob ? 'issue-code' : 'relevancy',
  });
  const {mutate: fetchDocListMasterId, reset: resetRelevancyMasterId} =
    useGetMasterDocIdForRelevancy();
  const {
    mutate: fetchDocListIssueCodingMasterId,
    reset: resetIssueCodeMasterId,
  } = useGetMasterDocIdForIssueCoding();
  const {mutate: fetchDocListErroredMasterId, reset: resetErroredMasterId} =
    useGetMasterDocIdErrorDoc();
  const totalDocsToView = useRef(0);
  const pendingDocListNav = useRef<{
    payload: IPayload;
    type: PAYLOAD_TYPE;
  } | null>(null);
  const {data: docListData, isError: isGetMasterIdError} =
    useGetDocListMasterIdJobDocs(goToDocListPayload);

  const lastEvent = useSelector(
    (state: RootState) => state.signalr?.lastEvent ?? null
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const lastEventRef = useRef(lastEvent);
  lastEventRef.current = lastEvent;
  const lastEventAtNavStartRef = useRef(lastEvent);
  const [docListNavAttempt, setDocListNavAttempt] = useState(0);
  const resetDocListNavErrors = () => {
    resetRelevancyMasterId();
    resetIssueCodeMasterId();
    resetErroredMasterId();
    queryClient.removeQueries({queryKey: ['GetDocListMasterIdForJobDocs']});
  };
  const beginDocListNavigation = () => {
    lastEventAtNavStartRef.current = lastEventRef.current;
    setMasterDocListID(null);
    setGoToDocListPayload({reviewJobId: null, type: 'all'});
    resetDocListNavErrors();
    setDocListNavAttempt((attempt) => attempt + 1);
  };
  const errorToNavigate = () => {
    pendingDocListNav.current = null;
    setPayloadAndType(null);
    setIsNavigatingToDocList(false);
    setShowDocLimitWarning(false);
    setMasterDocListID(null);
    setGoToDocListPayload({reviewJobId: null, type: 'all'});
    resetDocListNavErrors();
    setToastDetails({
      isOpen: true,
      message: t('ai.review.jobList.jobListActions.errorNavigateToDocView'),
      status: ToastType.Error,
    });
    return;
  };
  const setStatesToDefault = () => {
    pendingDocListNav.current = null;
    setPayloadAndType(null);
    setIsNavigatingToDocList(false);
    setMasterDocListID(null);
    setGoToDocListPayload({reviewJobId: null, type: 'all'});
    resetDocListNavErrors();
  };

  useEffect(() => {
    if (!isNavigatingToDocList) return;

    if (isGetMasterIdError) {
      errorToNavigate();
      return;
    }

    if (!lastEvent) return;
    if (lastEvent === lastEventAtNavStartRef.current) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    const masterId =
      PAYLOAD_TYPE.REVIEW_MATRIX === payloadAndType?.type
        ? docListData?.doclistMasterId
        : masterDocListID;

    if (masterId == null) return;

    if (
      PAYLOAD_TYPE.REVIEW_MATRIX === payloadAndType?.type &&
      !goToDocListPayload.reviewJobId
    ) {
      return;
    }

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(masterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        const backPath = `${window.location.pathname}${window.location.search}`;
        if (!backPath.toLowerCase().includes('ai/review')) return; // to avoid setting doc list back navigation
        dispatch(setDocListId(Number(masterId)));
        dispatch(
          setDocListBackNavigation({
            label: t('ai.review.header.title'),
            path: backPath,
          })
        );
        setStatesToDefault();
        navigate('/Document/DocList');

        return;
      }
    }
    if (
      notificationId === Number(masterId) &&
      navType === SourceTypeCode.DOCVIEW
    ) {
      setStatesToDefault();
      navigate('/DocumentViewer/DocView');
      return;
    }
    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      errorToNavigate();
      return;
    }
  }, [
    lastEvent,
    isNavigatingToDocList,
    isGetMasterIdError,
    payloadAndType,
    goToDocListPayload.reviewJobId,
    docListData?.doclistMasterId,
    masterDocListID,
  ]);

  useEffect(() => {
    if (lastEvent?.payload?.type !== 'ManageAIReviewJobsDataUpdated') {
      return;
    }

    const eventJobId = lastEvent?.payload?.payload?.jobId;
    if (eventJobId == null || jobId == null) {
      return;
    }

    if (Number(eventJobId) !== Number(jobId)) {
      return;
    }

    queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
  }, [jobId, lastEvent, queryClient]);

  const handleNavigateToDocList = (
    docsType: DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB,
    totalDocs: number = 0
  ) => {
    if (totalDocs === 0) return;
    totalDocsToView.current = totalDocs;
    const next = {
      payload: {
        reviewJobId: searchParams.get('jobId'),
        type: docsType,
      },
      type: PAYLOAD_TYPE.REVIEW_MATRIX,
    };
    pendingDocListNav.current = next;
    beginDocListNavigation();
    setShowDocLimitWarning(true);
    setIsNavigatingToDocList(true);
    setPayloadAndType(next);
  };

  const handleSetStates = (
    payload: IPayload,
    type: PAYLOAD_TYPE,
    docsToView: number = 0,
    destination: 'DOCLIST' | 'DOCVIEW' = 'DOCLIST'
  ) => {
    pendingDocListNav.current = {payload, type};
    beginDocListNavigation();
    setPayloadAndType({payload, type});
    setIsNavigatingToDocList(true);
    setDocNavDestination(
      destination === 'DOCVIEW'
        ? SourceTypeCode.DOCVIEW
        : SourceTypeCode.DOCLIST
    );
    if (
      type === PAYLOAD_TYPE.DOC_RESULT_RELEVANCY ||
      type === PAYLOAD_TYPE.DOC_RESULT_ISSUE_CODE ||
      type === PAYLOAD_TYPE.DOC_ERROR
    ) {
      totalDocsToView.current = docsToView;
      setShowDocLimitWarning(true);
    }
  };

  const handleDocErrorPanelOpen = () => {
    setIsDocErrorPanelOpen(!isDocErrorPanelOpen);
  };

  const handleViewInDocListActionChange = useCallback(
    (action: {enabled: boolean; onClick: () => void}) => {
      setViewInDocListAction(action);
    },
    []
  );

  const handleOpenRelevancyDocPanel = ({
    reviewJobSubmittedId,
    docId,
  }: IAIReviewRelevancyDocSelection) => {
    setSelectedRelevancyDoc({
      reviewjobid: jobId, // JobResults route ID is AVR-22456 reviewjobid.
      reviewJobSubmittedId, // Selected AVR-22455 row detail identity.
      docId,
    });
    setIsRelevancyDocsPanelOpen(true);
  };

  const resultTabs = useMemo<JobResultTabConfig[]>(() => {
    if (!isIssueCodingJob) {
      return [
        {
          id: 'docResults',
          label: t('ai.review.jobResult.tabs.docResults'),
          content: (
            <JobResultDataGrid
              jobId={jobId}
              type={normalizedJobType}
              handleSetStates={handleSetStates}
              onViewInDocListActionChange={handleViewInDocListActionChange}
              onOpenRelevancyDocPanel={handleOpenRelevancyDocPanel}
              setSelectedDocForPreview={setSelectedDocForPreview}
              hasGroundTruthSelection={hasGroundTruthSelection}
            />
          ),
        },
      ];
    }

    return [
      {
        id: 'viewByDocs',
        label: t('ai.review.jobResult.tabs.viewByDocs'),
        content: (
          <ViewByDocsDataGrid
            jobId={jobId}
            handleSetStates={handleSetStates}
            onViewInDocListActionChange={handleViewInDocListActionChange}
            setSelectedDocForPreview={setSelectedDocForPreview}
          />
        ),
      },
      {
        id: 'viewByIssueCode',
        label: t('ai.review.jobResult.tabs.viewByIssueCode'),
        content: (
          <JobResultDataGrid
            jobId={jobId}
            type={normalizedJobType}
            handleSetStates={handleSetStates}
            onViewInDocListActionChange={handleViewInDocListActionChange}
            hasGroundTruthSelection={hasGroundTruthSelection}
          />
        ),
      },
    ];
  }, [
    handleViewInDocListActionChange,
    hasGroundTruthSelection,
    isIssueCodingJob,
    jobId,
    normalizedJobType,
  ]);
  const handleCloseRelevancyDocPanel = () => {
    setIsRelevancyDocsPanelOpen(false);
  };

  const handleFetchRelevancyMasterId = (
    documentCount: number,
    payload: IPayload = pendingDocListNav.current?.payload ??
      payloadAndType?.payload
  ) => {
    fetchDocListMasterId(
      {
        ...payload,
        payloadBody: {...payload?.payloadBody, documentCount},
      },
      {
        onSuccess: (response) => {
          setMasterDocListID(response.doclistMasterId);
        },
        onError: (error: any) => {
          console.error(error);
          errorToNavigate();
        },
      }
    );
  };

  const handleFetchIssueCodingMasterId = (
    payload: IPayload = pendingDocListNav.current?.payload ??
      payloadAndType?.payload
  ) => {
    fetchDocListIssueCodingMasterId(payload, {
      onSuccess: (response) => {
        setMasterDocListID(response.doclistMasterId);
      },
      onError: (error: any) => {
        console.error(error);
        errorToNavigate();
      },
    });
  };

  const handleFetchErroredMasterId = (
    documentCount: number,
    payload: IPayload = pendingDocListNav.current?.payload ??
      payloadAndType?.payload
  ) => {
    fetchDocListErroredMasterId(
      {
        ...payload,
        payloadBody: {
          ...(payload as IGetMasterDocIdForRelevancyPayload)?.payloadBody,
          documentCount,
        },
      },
      {
        onSuccess: (response) => {
          setMasterDocListID(response.doclistMasterId);
        },
        onError: (error: any) => {
          console.error(error);
          errorToNavigate();
        },
      }
    );
  };

  const handleViewInDocList = (totalDocs: number) => {
    const pending = pendingDocListNav.current ?? payloadAndType;
    switch (pending?.type) {
      case PAYLOAD_TYPE.REVIEW_MATRIX:
        setGoToDocListPayload(pending.payload);
        break;

      case PAYLOAD_TYPE.DOC_RESULT_RELEVANCY:
        handleFetchRelevancyMasterId(totalDocs, pending.payload);
        break;
      case PAYLOAD_TYPE.DOC_RESULT_ISSUE_CODE:
        handleFetchIssueCodingMasterId(pending.payload);
        break;
      case PAYLOAD_TYPE.DOC_ERROR:
        handleFetchErroredMasterId(totalDocs, pending.payload);
        break;
    }
  };

  return (
    <>
      {toastDetails.isOpen && (
        <ArrToastNotification
          message={toastDetails.message}
          status={[toastDetails.status]}
          onClose={() =>
            setToastDetails({
              isOpen: false,
              message: '',
              status: ToastType.Success,
            })
          }
        />
      )}
      {isNavigatingToDocList && (
        <ArrLoadingSpinner overlay isOpen={isNavigatingToDocList} />
      )}
      <ArrDocMaxLimitWarning
        key={docListNavAttempt}
        totalDocumentCount={totalDocsToView.current}
        show={showDocLimitWarning}
        destination={docNavDestination}
        onContinue={(totalDocsToView: number) =>
          handleViewInDocList(totalDocsToView)
        }
        onCancel={() => {
          setShowDocLimitWarning(false);
          setIsNavigatingToDocList(false);
        }}
      />
      <Box className="job-result-container">
        <JobResultHeader
          jobDetails={getJobDetailData ?? null}
          variant={isIssueCodingJob ? 'issueCodingV2' : 'relevancyV2'}
          metricsDetails={reviewJobResult}
          onDocErrorPanelOpen={handleDocErrorPanelOpen}
          onNavigateToDocList={handleNavigateToDocList}
          onRefetchJobDetails={refetchJobDetails}
        />
        {reviewJobResult?.jobId && (
          <ArrDrawer
            isOpen={isDocErrorPanelOpen}
            onClose={handleDocErrorPanelOpen}
            ariaLabel="drawer content"
            size="large"
            side="right"
            showButton={true}
            showHeader={true}
            className="doc-error-panel"
          >
            <DocErrorPanel
              isDocErrorPanelOpen={isDocErrorPanelOpen}
              jobId={reviewJobResult.jobId}
              handleSetStates={handleSetStates}
              setSelectedDocForPreview={setSelectedDocForPreview}
            />
          </ArrDrawer>
        )}

        <Box className="job-result-container__tabs">
          <JobResultTabs
            value={activeResultTab}
            onChange={(_, nextTab) => {
              setActiveResultTab(nextTab);
              setViewInDocListAction({enabled: false, onClick: () => {}});
            }}
            tabs={resultTabs}
            isViewInDocListEnabled={viewInDocListAction.enabled}
            onViewInDocList={viewInDocListAction.onClick}
          />
        </Box>
        {!isIssueCodingJob && (
          <ArrDrawer
            isOpen={isRelevancyDocsPanelOpen}
            onClose={handleCloseRelevancyDocPanel}
            ariaLabel="relevancy document details"
            size="large"
            side="right"
            title={String(selectedRelevancyDoc?.docId ?? '')}
            showButton={true}
            showHeader={true}
            className="doc-relevancy-panel"
            closeButtonVariant="primary"
            disableResize={true}
          >
            <RelevancyDocsPanel
              isRelevancyDocsPanelOpen={isRelevancyDocsPanelOpen}
              reviewjobid={selectedRelevancyDoc?.reviewjobid}
              reviewJobSubmittedId={selectedRelevancyDoc?.reviewJobSubmittedId}
              docId={selectedRelevancyDoc?.docId}
            />
          </ArrDrawer>
        )}
      </Box>
      {!!selectedDocForPreview && (
        <DocPreviewModal
          showModal={!!selectedDocForPreview}
          onClose={() => setSelectedDocForPreview(null)}
          document={selectedDocForPreview}
        />
      )}
    </>
  );
};

export default JobResults;
